import { ChevronDown, ClipboardPenLine } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Button } from '~shared/shadcn/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '~shared/shadcn/ui/collapsible';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '~shared/shadcn/ui/form';
import { Input } from '~shared/shadcn/ui/input';
import { Textarea } from '~shared/shadcn/ui/textarea';

import type { PerformanceFormValues } from './edit-performance-sheet';

export const StudentSizesSection = () => {
  const { control, watch, setValue } = useFormContext<PerformanceFormValues>();
  const watchedValues = watch();

  const studentIds = useMemo(
    () => watchedValues.studentIds ?? [],
    [watchedValues.studentIds],
  );
  const studentSizes = useMemo(
    () => watchedValues.studentSizes ?? [],
    [watchedValues.studentSizes],
  );
  const bulkCostumeSize = useMemo(
    () => watchedValues.bulkCostumeSize,
    [watchedValues.bulkCostumeSize],
  );
  const bulkTopSize = useMemo(
    () => watchedValues.bulkTopSize,
    [watchedValues.bulkTopSize],
  );
  const bulkBottomSize = useMemo(
    () => watchedValues.bulkBottomSize,
    [watchedValues.bulkBottomSize],
  );
  const bulkTightsSize = useMemo(
    () => watchedValues.bulkTightsSize,
    [watchedValues.bulkTightsSize],
  );
  const bulkShoeSize = useMemo(
    () => watchedValues.bulkShoeSize,
    [watchedValues.bulkShoeSize],
  );

  useEffect(() => {
    if (studentIds.length === 0) {
      if (studentSizes.length > 0) {
        setValue('studentSizes', []);
      }
      return;
    }

    const sizeMap = new Map(
      studentSizes.map((size) => [size.studentId, size]),
    );

    const synchronizedSizes = studentIds.map((student) => {
      const existing = sizeMap.get(student.value);
      return {
        studentId: student.value,
        studentName: student.label,
        costumeSize: existing?.costumeSize ?? undefined,
        topSize: existing?.topSize ?? undefined,
        bottomSize: existing?.bottomSize ?? undefined,
        tightsSize: existing?.tightsSize ?? undefined,
        shoeSize: existing?.shoeSize ?? undefined,
        notes: existing?.notes ?? undefined,
      };
    });

    const needsUpdate =
      synchronizedSizes.length !== studentSizes.length ||
      synchronizedSizes.some((size, index) => {
        const current = studentSizes[index];
        if (!current) {
          return true;
        }

        return (
          size.studentId !== current.studentId ||
          size.studentName !== current.studentName
        );
      });

    if (needsUpdate) {
      setValue('studentSizes', synchronizedSizes);
    }
  }, [studentIds, studentSizes, setValue]);

  const applyBulkFill = () => {
    if (!studentSizes.length) return;
    const updatedSizes = studentSizes.map((student) => ({
      ...student,
      costumeSize: bulkCostumeSize ?? student.costumeSize,
      topSize: bulkTopSize ?? student.topSize,
      bottomSize: bulkBottomSize ?? student.bottomSize,
      tightsSize: bulkTightsSize ?? student.tightsSize,
      shoeSize: bulkShoeSize ?? student.shoeSize,
    }));
    setValue('studentSizes', updatedSizes);
  };

  const [open, setOpen] = useState(true);

  if (studentIds.length === 0) {
    return (
      <div className='py-4 text-center text-muted-foreground'>
        No students assigned to this performance
      </div>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen} className='space-y-6'>
      <CollapsibleTrigger asChild>
        <div
          className='flex cursor-pointer items-center justify-between rounded-md border-b bg-muted/20 px-3 py-2 transition-colors hover:bg-muted/40'
          onClick={() => {
            setOpen(!open);
          }}
        >
          <div>
            <h3 className='text-lg font-semibold'>Student Sizes</h3>
            <p className='text-sm text-muted-foreground'>
              Manage costume and shoe sizes for all students
            </p>
          </div>
          <Button variant='ghost' size='icon' className='pointer-events-none'>
            <ChevronDown
              className={`size-5 transition-transform ${open ? 'rotate-180' : ''}`}
            />
          </Button>
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent className='space-y-6'>
        <div className='space-y-2 rounded-lg border p-4'>
          <div className='mb-2 flex items-center gap-2'>
            <ClipboardPenLine className='size-4' />
            <h4 className='font-medium'>Bulk Fill</h4>
          </div>
          <p className='mb-4 text-sm text-muted-foreground'>
            Applies only to blank fields. Existing values will not be
            overwritten.
          </p>

          <div className='grid grid-cols-5 gap-3'>
            <FormField
              control={control}
              name='bulkCostumeSize'
              render={({ field }) => (
                <FormItem className='col-span-3'>
                  <FormLabel className='text-xs'>Costume Size</FormLabel>
                  <FormControl>
                    <Input placeholder='CM' {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name='bulkTopSize'
              render={({ field }) => (
                <FormItem className='col-span-2'>
                  <FormLabel className='text-xs'>Top</FormLabel>
                  <FormControl>
                    <Input placeholder='S' {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className='grid grid-cols-3 gap-3'>
            <FormField
              control={control}
              name='bulkBottomSize'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-xs'>Skirt/Pant</FormLabel>
                  <FormControl>
                    <Input placeholder='S' {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name='bulkTightsSize'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-xs'>Tights</FormLabel>
                  <FormControl>
                    <Input placeholder='M' {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name='bulkShoeSize'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-xs'>Shoe</FormLabel>
                  <FormControl>
                    <Input placeholder='7.5' {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className='flex justify-end pt-2'>
            <Button type='button' variant='secondary' onClick={applyBulkFill}>
              Apply to Empty Fields
            </Button>
          </div>
        </div>

        <div className='space-y-4'>
          {studentSizes.map((student, index) => (
            <div
              key={student.studentId}
              className='space-y-2 rounded-lg border p-4'
            >
              <h4 className='text-lg font-medium'>
                {student.studentName || `Student ID: ${student.studentId}`}
              </h4>

              <div className='grid grid-cols-5 gap-3'>
                <FormField
                  control={control}
                  name={`studentSizes.${index}.costumeSize`}
                  render={({ field }) => (
                    <FormItem className='col-span-3'>
                      <FormLabel className='text-xs'>Costume Size</FormLabel>
                      <FormControl>
                        <Input placeholder='CM' {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name={`studentSizes.${index}.topSize`}
                  render={({ field }) => (
                    <FormItem className='col-span-2'>
                      <FormLabel className='text-xs'>Top</FormLabel>
                      <FormControl>
                        <Input placeholder='S' {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className='grid grid-cols-3 gap-3'>
                <FormField
                  control={control}
                  name={`studentSizes.${index}.bottomSize`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className='text-xs'>Skirt/Pant</FormLabel>
                      <FormControl>
                        <Input placeholder='S' {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name={`studentSizes.${index}.tightsSize`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className='text-xs'>Tights</FormLabel>
                      <FormControl>
                        <Input placeholder='M' {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name={`studentSizes.${index}.shoeSize`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className='text-xs'>Shoe</FormLabel>
                      <FormControl>
                        <Input placeholder='7.5' {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={control}
                name={`studentSizes.${index}.notes`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='text-xs'>Notes</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder='Any special notes...'
                        className='resize-none'
                        {...field}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
